import React, { useEffect, useState } from "react";
import { Form, message } from "antd";
import AppInput from "../../../components/common/AppInput";
import AppButton from "../../../components/common/AppButton";
import AntdModal from "../../../components/common/AntdModal";
import { isEmpty, map } from "lodash";
import { inputNumberFormatter } from "../../../config/constants";
import useMonthlyPayslipStore from "../../../config/stores/administration/useMonthlyPayslipStore";
import useStaffContractsStore from "../../../config/stores/administration/useStaffContractStore";
import dayjs from "dayjs";
import axios from "axios";

const monthOptions = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

const borderless = { variant: "borderless" };

const PayslipForm = () => {
  const [form] = Form.useForm();
  const [payrollData, setPayrollData] = useState(null);
  const [loadingPayrollData, setLoadingPayrollData] = useState(false);
  const [hasPayrollData, setHasPayrollData] = useState(false);
  
  const payslipStore = useMonthlyPayslipStore();
  const staffContractStore = useStaffContractsStore();
  const {
    showCreatePayslip,
    setCreatingPayslip,
    creating,
    createPayslip,
    updatePayslip,
    creatingPayslip,
    updatingPayslip,
    payslipToEdit,
  } = payslipStore;
  
  const { metaData, getMetaData, gettingMetaData } = staffContractStore;

  useEffect(() => {
    if (!metaData?.users) {
      getMetaData();
    }
  }, [metaData, getMetaData]);

  useEffect(() => {
    if (creating) {
      form.resetFields();
      setPayrollData(null);
      setHasPayrollData(false);
    } else if (payslipToEdit) {
      // Convert year to a date object for the date picker
      const yearDate = payslipToEdit.year ? dayjs(`${payslipToEdit.year}-01-01`) : null;
      form.setFieldsValue({ ...payslipToEdit, year: yearDate });
      setPayrollData(null);
      setHasPayrollData(false);
    }
  }, [creating, payslipToEdit, form]);

  // Watch for user_id, month, and year to fetch payroll data
  const selectedUserId = Form.useWatch("user_id", form);
  const selectedMonth = Form.useWatch("month", form);
  const selectedYear = Form.useWatch("year", form);


  // Fetch payroll data when user, month, and year are selected
  useEffect(() => {
    const fetchPayrollData = async () => {
      if (creating && selectedUserId && selectedMonth && selectedYear) {
        setLoadingPayrollData(true);
        try {
          // Handle month - ensure it's a number (1-12)
          let monthValue = null;
          if (selectedMonth) {
            if (typeof selectedMonth === 'number') {
              monthValue = selectedMonth;
            } else if (typeof selectedMonth === 'string') {
              monthValue = parseInt(selectedMonth);
            } else {
              monthValue = selectedMonth;
            }
          }

          // Handle year - can be dayjs object, number, or string
          let yearValue = null;
          if (selectedYear) {
            if (dayjs.isDayjs(selectedYear)) {
              yearValue = selectedYear.year();
            } else if (typeof selectedYear === 'number') {
              yearValue = selectedYear;
            } else if (typeof selectedYear === 'string') {
              yearValue = parseInt(selectedYear);
            } else {
              yearValue = dayjs(selectedYear).year();
            }
          }

          if (!monthValue || isNaN(monthValue) || monthValue < 1 || monthValue > 12) {
            setLoadingPayrollData(false);
            return;
          }

          if (!yearValue || isNaN(yearValue)) {
            setLoadingPayrollData(false);
            return;
          }

          const apiUrl = `/administration/monthly-payslips/payroll-data`;
          
          const res = await axios.get(apiUrl, {
            params: {
              user_id: selectedUserId,
              month: monthValue,
              year: yearValue,
            },
          });

          // Backend returns via http.serverSuccess: { success: true, message: "...", data: { payrollData: {...}, exists: true } }
          // With axios interceptor, res might be directly the data or { data: {...} }
          // So we need to check: res.data.data.payrollData OR res.data.payrollData OR res.payrollData
          const responseData = res?.data || res;
          
          // Extract payrollData from the nested structure
          // Backend returns: { success: true, message: "...", data: { payrollData: {...} or null, exists: true/false } }
          // So we need: res.data.data.payrollData
          let payrollDataFromAPI = null;
          let exists = false;
          
          // Check if payroll data exists flag
          if (responseData?.data?.exists !== undefined) {
            exists = responseData.data.exists;
          } else if (responseData?.exists !== undefined) {
            exists = responseData.exists;
          }
          
          if (responseData?.data?.payrollData) {
            // Standard structure: responseData.data.payrollData
            payrollDataFromAPI = responseData.data.payrollData;
          } else if (responseData?.payrollData) {
            // Alternative: responseData.payrollData (if interceptor unwraps)
            payrollDataFromAPI = responseData.payrollData;
          } else if (responseData?.data && typeof responseData.data === 'object') {
            // Check if data itself is the payrollData
            if (responseData.data.gross_pay !== undefined || responseData.data.grossPay !== undefined) {
              payrollDataFromAPI = responseData.data;
            }
          }

          // Handle case when no payroll data exists (this is normal - payroll must be created first)
          if (!exists || !payrollDataFromAPI || payrollDataFromAPI === null) {
            setPayrollData(null);
            setHasPayrollData(false);
            setLoadingPayrollData(false);
            return;
          }

          // Check if payroll data exists and has valid structure
          if (payrollDataFromAPI && payrollDataFromAPI !== null && typeof payrollDataFromAPI === 'object' && (payrollDataFromAPI.gross_pay !== undefined || payrollDataFromAPI.grossPay !== undefined)) {
            // Handle both snake_case and camelCase field names
            const payrollData = {
              gross_pay: payrollDataFromAPI.gross_pay || payrollDataFromAPI.grossPay || 0,
              paye: payrollDataFromAPI.paye || 0,
              nssf_deduction: payrollDataFromAPI.nssf_deduction || payrollDataFromAPI.nssfDeduction || 0,
              total_deductions: payrollDataFromAPI.total_deductions || payrollDataFromAPI.totalDeductions || 0,
              net_salary: payrollDataFromAPI.net_salary || payrollDataFromAPI.netSalary || 0,
              transport_allowance: payrollDataFromAPI.transport_allowance || payrollDataFromAPI.transportAllowance || 0,
              housing_allowance: payrollDataFromAPI.housing_allowance || payrollDataFromAPI.housingAllowance || 0,
              medical_allowances: payrollDataFromAPI.medical_allowances || payrollDataFromAPI.medicalAllowances || 0,
              other_allowances: payrollDataFromAPI.other_allowances || payrollDataFromAPI.otherAllowances || 0,
              total_earnings: payrollDataFromAPI.total_earnings || payrollDataFromAPI.totalEarnings || 0,
              salary_advance: payrollDataFromAPI.salary_advance || payrollDataFromAPI.salaryAdvance || 0,
              staff_savings_scheme: payrollDataFromAPI.staff_savings_scheme || payrollDataFromAPI.staffSavingsScheme || 0,
              local_service_tax: payrollDataFromAPI.local_service_tax || payrollDataFromAPI.localServiceTax || 0,
              nssf_number: payrollDataFromAPI.nssf_number || payrollDataFromAPI.nssfNumber || "",
              total_working_days: payrollDataFromAPI.total_working_days || payrollDataFromAPI.totalWorkingDays || null,
              paid_days: payrollDataFromAPI.paid_days || payrollDataFromAPI.paidDays || null,
              lop_days: payrollDataFromAPI.lop_days || payrollDataFromAPI.lopDays || null,
              leaves_taken: payrollDataFromAPI.leaves_taken || payrollDataFromAPI.leavesTaken || null,
            };

            setPayrollData(payrollData);
            setHasPayrollData(true);

            // Auto-populate calculated fields (read-only from payroll)
            // Only set manual fields if they have values from payroll, otherwise leave them empty/editable
            const formValues = {
              // Calculated fields from payroll (read-only)
              gross_pay: payrollData.gross_pay,
              paye: payrollData.paye,
              nssf_deduction: payrollData.nssf_deduction,
              total_deductions: payrollData.total_deductions,
              net_salary: payrollData.net_salary,
              transport_allowance: payrollData.transport_allowance,
              housing_allowance: payrollData.housing_allowance,
              medical_allowances: payrollData.medical_allowances,
              other_allowances: payrollData.other_allowances,
              total_earnings: payrollData.total_earnings,
              salary_advance: payrollData.salary_advance,
              staff_savings_scheme: payrollData.staff_savings_scheme,
              local_service_tax: payrollData.local_service_tax,
            };

            // Only pre-populate manual fields if they have values (these remain editable)
            if (payrollData.nssf_number) {
              formValues.nssf_number = payrollData.nssf_number;
            }
            if (payrollData.total_working_days !== null && payrollData.total_working_days !== undefined) {
              formValues.total_working_days = payrollData.total_working_days;
            }
            if (payrollData.paid_days !== null && payrollData.paid_days !== undefined) {
              formValues.paid_days = payrollData.paid_days;
            }
            if (payrollData.lop_days !== null && payrollData.lop_days !== undefined) {
              formValues.lop_days = payrollData.lop_days;
            }
            if (payrollData.leaves_taken !== null && payrollData.leaves_taken !== undefined) {
              formValues.leaves_taken = payrollData.leaves_taken;
            }

            form.setFieldsValue(formValues);
          } else {
            setPayrollData(null);
            setHasPayrollData(false);
          }
        } catch (error) {
          // Silently handle errors - don't show permission errors or other errors
          // This allows users to manually enter payslip data even if payroll data fetch fails
          // Only log to console in development if needed, but don't show error notifications
          setPayrollData(null);
          setHasPayrollData(false);
        } finally {
          setLoadingPayrollData(false);
        }
      } else {
        setPayrollData(null);
        setHasPayrollData(false);
      }
    };

    fetchPayrollData();
  }, [selectedUserId, selectedMonth, selectedYear, creating, form]);

  // Watch each earning and deduction field individually
  const gross_pay = Form.useWatch("gross_pay", form);
  const transport_allowance = Form.useWatch("transport_allowance", form);
  const housing_allowance = Form.useWatch("housing_allowance", form);
  const medical_allowances = Form.useWatch("medical_allowances", form);
  const other_allowances = Form.useWatch("other_allowances", form);

  const nssf_deduction = Form.useWatch("nssf_deduction", form);
  const paye = Form.useWatch("paye", form);
  const local_service_tax = Form.useWatch("local_service_tax", form);
  const salary_advance = Form.useWatch("salary_advance", form);
  const staff_savings_scheme = Form.useWatch("staff_savings_scheme", form);

  const total_earnings = Form.useWatch("total_earnings", form);
  const total_deductions = Form.useWatch("total_deductions", form);

  useEffect(() => {
    // Calculate total earnings (only if not using payroll data, or if payroll data doesn't have it)
    if (!hasPayrollData || !payrollData?.total_earnings) {
      const totalEarnings =
        (Number(gross_pay) || 0) +
        (Number(transport_allowance) || 0) +
        (Number(housing_allowance) || 0) +
        (Number(medical_allowances) || 0) +
        (Number(other_allowances) || 0);
      if (!isNaN(totalEarnings)) {
        form.setFieldsValue({ total_earnings: totalEarnings });
      }
    }
  }, [gross_pay, transport_allowance, housing_allowance, medical_allowances, other_allowances, form, hasPayrollData, payrollData]);

  useEffect(() => {
    // Calculate total deductions (only if not using payroll data, or if payroll data doesn't have it)
    if (!hasPayrollData || !payrollData?.total_deductions) {
      const totalDeductions =
        (Number(nssf_deduction) || 0) +
        (Number(paye) || 0) +
        (Number(local_service_tax) || 0) +
        (Number(salary_advance) || 0) +
        (Number(staff_savings_scheme) || 0);
      if (!isNaN(totalDeductions)) {
        form.setFieldsValue({ total_deductions: totalDeductions });
      }
    }
  }, [nssf_deduction, paye, local_service_tax, salary_advance, staff_savings_scheme, form, hasPayrollData, payrollData]);

  // Auto-calculate net salary (only if not using payroll data, or if payroll data doesn't have it)
  useEffect(() => {
    if (!hasPayrollData || !payrollData?.net_salary) {
      const netSalary = (Number(total_earnings) || 0) - (Number(total_deductions) || 0);
      if (!isNaN(netSalary)) {
        form.setFieldsValue({ net_salary: netSalary });
      }
    }
  }, [total_earnings, total_deductions, form, hasPayrollData, payrollData]);

  const closeModal = () => {
    setCreatingPayslip(false, false, null);
    form.resetFields();
    setPayrollData(null);
    setHasPayrollData(false);
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        // Extract year as a number
        const year = values.year ? dayjs(values.year).year() : undefined;
        const payload = { ...values, year };
        if (creating) {
          await createPayslip({
            ...payload,
            year: dayjs(values.year).year()
          });

          if (isEmpty(payslipStore.creatingPayslipError)) {
            closeModal();
        }
     
        } else {
         await  updatePayslip(payslipToEdit.id, payload);
         if (isEmpty(payslipStore.updatingPayslipError)) {
            closeModal();
        }
        }


     
      })
      .catch(() => {
        message.error("Please fill in all required fields correctly");
      });
  };

  return (
    <AntdModal
      title={creating ? "CREATE PAYSLIP" : "EDIT PAYSLIP"}
      open={showCreatePayslip}
      closeModal={closeModal}
      customSize={1300}
      footer={[
        <AppButton
          key="save"
          text={creating ? "CREATE" : "UPDATE"}
          loading={creatingPayslip || updatingPayslip}
          onClick={onSubmit}
        />, 
        <AppButton key="cancel" text="CANCEL" onClick={closeModal} danger />
      ]}
    >
      {loadingPayrollData && (
        <div className="text-center p-2">
          <span className="text-muted">Loading payroll data...</span>
        </div>
      )}
      {hasPayrollData && (
        <div className="alert alert-info m-2">
          <strong>Note:</strong> Calculated fields are auto-populated from payroll data and are read-only. You can edit manual fields (NSSF Number, Days, etc.).
        </div>
      )}
      {creating && selectedUserId && (!selectedMonth || !selectedYear) && (
        <div className="alert alert-warning m-2">
          <strong>Note:</strong> Please select Month and Year to auto-populate calculated fields from payroll data.
        </div>
      )}
      {creating && selectedUserId && selectedMonth && selectedYear && !hasPayrollData && !loadingPayrollData && (
        <div className="alert alert-info m-2">
          <strong>Info:</strong> No payroll data found for this employee/month/year combination.
          <br />
          <small>You can manually enter all values, or create the payroll entry first in the <strong>Payroll</strong> module to auto-populate these fields.</small>
        </div>
      )}
      <Form layout="vertical" form={form}>
        <div className="p-2">
          <table className="table table-bordered table-sm table-responsive">
            <tbody>
              <tr>
                <td className="fw-bold" style={{ width: "15%" }}>Employee</td>
                <td colSpan={7}>
                  <AppInput
                    label={null}
                    name="user_id"
                    type="select"
                    rules={[{ required: true, message: "Employee is required" }]}
                    options={map(metaData?.users, (user) => ({
                      label: `${user.first_name} ${user.other_names}`,
                      value: user.id,
                    }))}
                    inputAttributes={{ ...borderless, loading: gettingMetaData, placeholder: "Select employee" }}
                  />
                </td>
              </tr>
              <tr>
                <td className="fw-bold">Month</td>
                <td>
                  <AppInput
                    name="month"
                    type="select"
                    options={monthOptions}
                    rules={[{ required: true, message: "Month is required" }]}
                    inputAttributes={{ ...borderless, placeholder: "Select month" }}
                  />
                </td>
                <td className="fw-bold">Year</td>
                <td>
                  <AppInput 
                    name="year"
                    type="date"
                    inputAttributes={{ ...borderless, placeholder: "Enter year", picker: "year" }}
                  />
                </td>
                <td className="fw-bold">NSSF Number</td>
                <td colSpan={3}>
                  <AppInput 
                    label={null} 
                    name="nssf_number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter NSSF number",
                      readOnly: false, // Always editable
                      disabled: false 
                    }} 
                  />
                </td>
              </tr>
              <tr>
                <td className="fw-bold">Total Working Days</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="total_working_days" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter total working days", 
                      formatter: inputNumberFormatter,
                      readOnly: false, // Always editable
                      disabled: false 
                    }} 
                  />
                </td>
                <td className="fw-bold">Paid Days</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="paid_days" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter paid days", 
                      formatter: inputNumberFormatter,
                      readOnly: false, // Always editable
                      disabled: false 
                    }} 
                  />
                </td>
                <td className="fw-bold">Loss of Pay (LOP) days</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="lop_days" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter LOP days", 
                      formatter: inputNumberFormatter,
                      readOnly: false, // Always editable
                      disabled: false 
                    }} 
                  />
                </td>
                <td className="fw-bold">Leaves Taken</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="leaves_taken" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter leaves taken", 
                      formatter: inputNumberFormatter,
                      readOnly: false, // Always editable
                      disabled: false 
                    }} 
                  />
                </td>
              </tr>
              <tr className="table-active">
                <td colSpan={4} className="fw-bold text-center">Earnings</td>
                <td colSpan={4} className="fw-bold text-center">Deductions</td>
              </tr>
              <tr>
                <td className="fw-bold">Gross Pay {hasPayrollData && <span className="text-muted">(from payroll)</span>}</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="gross_pay" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: hasPayrollData ? "From payroll" : "Enter gross pay", 
                      readOnly: hasPayrollData,
                      formatter: inputNumberFormatter 
                    }} 
                  />
                </td>
                <td className="fw-bold">Transport Allowance</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="transport_allowance" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter transport allowance", 
                      readOnly: false, // Always editable
                      formatter: inputNumberFormatter 
                    }} 
                  />
                </td>
                <td className="fw-bold">NSSF (15%) {hasPayrollData && <span className="text-muted">(from payroll)</span>}</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="nssf_deduction" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: hasPayrollData ? "From payroll" : "Enter NSSF deduction", 
                      readOnly: hasPayrollData,
                      formatter: inputNumberFormatter 
                    }} 
                  />
                </td>
                <td className="fw-bold">PAYE {hasPayrollData && <span className="text-muted">(from payroll)</span>}</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="paye" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: hasPayrollData ? "From payroll" : "Enter PAYE", 
                      readOnly: hasPayrollData,
                      formatter: inputNumberFormatter 
                    }} 
                  />
                </td>
              </tr>
              <tr>
                <td className="fw-bold">Housing Allowance</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="housing_allowance" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter housing allowance", 
                      readOnly: false, // Always editable
                      formatter: inputNumberFormatter 
                    }} 
                  />
                </td>
                <td className="fw-bold">Medical Allowances</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="medical_allowances" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter medical allowances", 
                      readOnly: false, // Always editable
                      formatter: inputNumberFormatter 
                    }} 
                  />
                </td>
                <td className="fw-bold">Local Service Tax</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="local_service_tax" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter local service tax", 
                      readOnly: false, // Always editable
                      formatter: inputNumberFormatter 
                    }} 
                  />
                </td>
                <td className="fw-bold">Salary Advance</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="salary_advance" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter salary advance", 
                      readOnly: false, // Always editable
                      formatter: inputNumberFormatter 
                    }} 
                  />
                </td>
              </tr>
              <tr>
                <td className="fw-bold">Other Allowances</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="other_allowances" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter other allowances", 
                      readOnly: false, // Always editable
                      formatter: inputNumberFormatter 
                    }} 
                  />
                </td>
                <td className="fw-bold">Total Earnings</td>
                <td>
                  <AppInput label={null} name="total_earnings" type="number" inputAttributes={{ ...borderless, placeholder: "Auto-calculated", readOnly: true, formatter: inputNumberFormatter }} />
                </td>
                <td className="fw-bold">Staff Savings Scheme</td>
                <td>
                  <AppInput 
                    label={null} 
                    name="staff_savings_scheme" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: "Enter staff savings scheme", 
                      readOnly: false, // Always editable
                      formatter: inputNumberFormatter 
                    }} 
                  />
                </td>
                <td className="fw-bold">Total Deductions</td>
                <td>
                  <AppInput label={null} name="total_deductions" type="number" inputAttributes={{ ...borderless, placeholder: "Auto-calculated", readOnly: true, formatter: inputNumberFormatter }} />
                </td>
              </tr>
              <tr className="table-active">
                <td colSpan={6} className="fw-bold text-end">Net Salary {hasPayrollData && <span className="text-muted">(from payroll)</span>}</td>
                <td colSpan={2}>
                  <AppInput 
                    label={null} 
                    name="net_salary" 
                    type="number" 
                    inputAttributes={{ 
                      ...borderless, 
                      placeholder: hasPayrollData ? "From payroll" : "Auto-calculated", 
                      readOnly: hasPayrollData, // Read-only only when payroll data exists
                      formatter: inputNumberFormatter 
                    }} 
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Form>
    </AntdModal>
  );
};

export default PayslipForm; 